/**
 * AddMissingItemsModal - Modal for adding missing items from search terms
 * 
 * When a user searches for comma-separated items and some don't exist,
 * this modal lets them review and add the missing ones.
 * Uses the receipt endpoint with duplicate_action: 'use_existing' so that:
 * - Items already in inventory get linked to the current collection
 * - Truly new items get created with qty=0 (need to buy)
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { API_ENDPOINTS } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useSpace } from '../contexts/SpaceContext';
import { hasAIConsent } from './AIConsentModal';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/addItemModal.css';

const MINOR_WORDS = new Set(['a', 'an', 'the', 'and', 'but', 'or', 'nor', 'for', 'yet', 'so', 'of', 'in', 'on', 'at', 'to', 'by', 'with', 'from', 'up', 'as']);

const toTitleCase = (str: string): string => {
  return str.split(' ').map((word, index) => {
    if (!word) return word;
    const lower = word.toLowerCase();
    if (index === 0 || !MINOR_WORDS.has(lower)) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return lower;
  }).join(' ');
};

interface MissingItem {
  id: number;
  name: string;
  qty: number;
  selected: boolean;
}

interface AddMissingItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  missingTerms: string[];
  collectionId?: number | null;
  collectionName?: string;
  onItemsAdded?: () => void;
}

const AddMissingItemsModal: React.FC<AddMissingItemsModalProps> = ({
  isOpen,
  onClose,
  missingTerms,
  collectionId,
  collectionName,
  onItemsAdded,
}) => {
  const { token, user } = useAuth();
  const { activeSpace } = useSpace();
  const [items, setItems] = useState<MissingItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize items from missingTerms when modal opens
  useEffect(() => {
    if (isOpen && missingTerms.length > 0) {
      setItems(missingTerms.map((term, idx) => {
        const clean = term.startsWith('"') && term.endsWith('"') ? term.slice(1, -1) : term;
        return {
          id: idx,
          name: toTitleCase(clean.trim()),
          qty: 0,
          selected: true,
        };
      }));
      setError(null);
    }
  }, [isOpen, missingTerms]);

  const toggleItem = (id: number) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, selected: !item.selected } : item
    ));
  };

  const updateItemName = (id: number, name: string) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, name } : item
    ));
  };

  const updateItemQty = (id: number, qty: number) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, qty: Math.max(0, qty) } : item
    ));
  };

  const setAllQty = (qty: number) => {
    setItems(prev => prev.map(item => ({ ...item, qty })));
  };

  const combineItems = (topIndex: number) => {
    setItems(prev => {
      const top = prev[topIndex];
      const bottom = prev[topIndex + 1];
      if (!top || !bottom) return prev;
      const combined: MissingItem = {
        id: top.id,
        name: toTitleCase((top.name.trim() + ' ' + bottom.name.trim().toLowerCase()).trim()),
        qty: Math.max(top.qty, bottom.qty),
        selected: top.selected || bottom.selected,
      };
      const next = [...prev];
      next.splice(topIndex, 2, combined);
      return next;
    });
  };

  const handleSave = async () => {
    const selectedItems = items.filter(i => i.selected && i.name.trim());
    if (selectedItems.length === 0) return;

    setIsSaving(true);
    setError(null);

    try {
      const requestBody: any = {
        items: selectedItems.map(item => ({
          name: item.name.trim(),
          quantity: item.qty,
        })),
        duplicate_action: 'use_existing',
        auto_categorize: hasAIConsent(user?.id),
      };

      if (collectionId) {
        requestBody.collection_id = collectionId;
      }
      requestBody.space_id = activeSpace?.id;

      const response = await fetch(API_ENDPOINTS.ADD_RECEIPT_TO_COLLECTION, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add items');
      }

      onItemsAdded?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add items');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const selectedCount = items.filter(i => i.selected && i.name.trim()).length;

  return createPortal(
    <div className="add-item-modal-overlay" onClick={onClose}>
      <div
        className="add-item-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '70vh' }}
      >
        {/* Header */}
        <div className="add-item-modal-header">
          <h2>Add Missing Items</h2>
          {collectionName && (
            <span className="add-item-collection-badge">→ {collectionName}</span>
          )}
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Content */}
        <div className="add-item-modal-content" style={{ padding: '0 16px' }}>
          <p style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            margin: '0 0 12px 0',
          }}>
            {items.length} item{items.length !== 1 ? 's' : ''} not found. Select which to add:
          </p>

          {error && <div className="add-item-error">{error}</div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    background: item.selected ? 'var(--color-bg-secondary)' : 'transparent',
                    transition: 'background 0.15s',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={item.selected}
                    onChange={() => toggleItem(item.id)}
                    style={{ width: '18px', height: '18px', flexShrink: 0 }}
                  />
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => updateItemName(item.id, e.target.value)}
                    onBlur={(e) => updateItemName(item.id, toTitleCase(e.target.value))}
                    style={{
                      flex: 1,
                      border: 'none',
                      background: 'transparent',
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--color-text-primary)',
                      padding: '4px 0',
                      outline: 'none',
                      opacity: item.selected ? 1 : 0.4,
                    }}
                  />
                                  </label>
                {/* Combine button between adjacent rows */}
                {index < items.length - 1 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 10px',
                    margin: '-2px 0',
                  }}>
                    <button
                      onClick={() => combineItems(index)}
                      title={`Combine "${item.name}" + "${items[index + 1].name}"`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '1px 8px',
                        fontSize: '11px',
                        color: 'var(--color-text-muted)',
                        background: 'transparent',
                        border: '1px dashed var(--color-border)',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        opacity: 0.6,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'var(--color-bg-tertiary)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; e.currentTarget.style.background = 'transparent'; }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 15l-6-6-6 6"/>
                      </svg>
                      join
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </button>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="add-item-modal-footer">
          <button
            className="add-item-btn-cancel"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="add-item-btn-save"
            onClick={handleSave}
            disabled={isSaving || selectedCount === 0}
          >
            {isSaving ? 'Adding...' : `Add ${selectedCount} Item${selectedCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddMissingItemsModal;
